/**
 * "My inventions" — the whole screen (Story #5648 build v2, figures S4 and
 * S4b), in the figure's order and nothing else:
 *
 *   Personal › My inventions                    <- the spine's single crumb
 *   My inventions  (6 inventions)  ?            [+ New invention]
 *   Every invention you own — as a list, or on one atlas …
 *   ┌ NEXT AT MY INVENTIONS · REVIEW OVERLAP …                              ┐
 *   List · Atlas · Synergy · Overlap · Shape & Strategy · Watch
 *   <the view>
 *
 * REMOVED FROM THE OLD PAGE, deliberately: the "Projects" page header, the
 * duplicate "My inventions" chip that used to sit above the tabs, and the old
 * stacked breadcrumb rows (the layout renders the one crumb this screen
 * declares on the spine). None of them is in the figure, so none of them is
 * left above or below the new content.
 *
 * The screen owns the CHROME. Each tab's body is its own component and fetches
 * its own own-scope payloads, so a slow or failing view degrades alone.
 */
import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { AlertCircle, Plus, RefreshCw } from 'lucide-react'
import PageHeader from '../ui/PageHeader'
import EmptyState from '../ui/EmptyState'
import LoadingSkeleton from '../shared/LoadingSkeleton'
import ScopeRail from '../shell/ScopeRail'
import { useSetSpine } from '../spine'
import { useAggregator } from '../../contexts/AggregatorContext'
import { useWorkspace } from '../../contexts/WorkspaceContext'
import OwnNextStepBanner from './OwnNextStepBanner'
import { useOwnNextStep } from './useOwnNextStep'
import MyInventionsAtlas from './MyInventionsAtlas'
import OwnOverlapView from './OwnOverlapView'
import OwnSynergyView from './OwnSynergyView'
import OwnShapeStrategyView from './OwnShapeStrategyView'
import OwnWatchView from './OwnWatchView'
import ProjectsTable from '../projects/ProjectsTable'
import { OWN_SCOPE_DESCRIPTION, ownScopeTabFor, type OwnScopeView } from './ownScopeViews'
import type { Project } from '../../types'

export interface MyInventionsScreenProps {
  view: OwnScopeView
  projects: Project[] | undefined
  isLoading: boolean
  isError: boolean
  error?: unknown
  refetch: () => void
}

export default function MyInventionsScreen({
  view,
  projects,
  isLoading,
  isError,
  error,
  refetch,
}: MyInventionsScreenProps) {
  const navigate = useNavigate()
  const { active } = useWorkspace()
  const aggregator = useAggregator()

  // Owned vs shared-in, by the same rule ProjectsTable and HomeDashboard use.
  const owned = useMemo(() => (projects ?? []).filter((p) => !p.shared_with_me), [projects])
  const nextStep = useOwnNextStep(owned)

  const firstInvention = owned[0]
  // Optional chaining, not laziness: a caller can be an inventor with no
  // portfolio at all, and the zoom-out reason must not depend on the list
  // having loaded.
  const portfolioName = aggregator.memberships?.[0]?.aggregator_name

  // The spine spec. Every value is real: the count is the roster's own length,
  // the steps waiting are the queue's own length, and the zoom-in target is an
  // invention that actually exists.
  useSetSpine({
    level: 'my-inventions',
    levels: ['invention', 'my-inventions'],
    links: firstInvention ? { invention: `/project/${firstInvention.project_id}` } : undefined,
    youreAt: [`${owned.length} ${owned.length === 1 ? 'invention' : 'inventions'}`],
    stepsWaiting: nextStep.queue.waiting.length,
    zoomIn: firstInvention
      ? { to: `/project/${firstInvention.project_id}`, title: `Zoom in: Invention — ${firstInvention.name}` }
      : { title: 'Zoom in — you have no invention to open yet' },
    zoomOut: portfolioName
      ? { title: `This is the widest view of your own inventions — switch to ${portfolioName} to see the portfolio` }
      : { title: 'This is the widest view of your own inventions' },
    crumb: [active.name, 'My inventions'],
  })

  const tab = ownScopeTabFor(view)

  return (
    <div data-part="MyInventionsScreen">
      <PageHeader
        title="My inventions"
        description={OWN_SCOPE_DESCRIPTION}
        helpKey={tab.helpKey}
        status={
          projects === undefined
            ? undefined
            : { label: `${owned.length} ${owned.length === 1 ? 'invention' : 'inventions'}`, tone: 'info' }
        }
        primaryAction={
          <button
            data-tour="new-disclosure"
            onClick={() => navigate('/new')}
            className="flex items-center gap-2 rounded-lg bg-iflow-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-iflow-700"
          >
            <Plus size={16} aria-hidden /> New invention
          </button>
        }
      />

      <OwnNextStepBanner
        queue={nextStep.queue}
        onSnooze={nextStep.onSnooze}
        onDrop={nextStep.onDrop}
        onRestore={nextStep.onRestore}
        loading={nextStep.loading}
      />

      <ScopeRail view={view} />

      {isLoading ? (
        <LoadingSkeleton rows={6} type={view === 'list' ? 'table' : 'card'} />
      ) : isError ? (
        <EmptyState
          icon={AlertCircle}
          title="We could not load your inventions"
          description={(error as Error)?.message || 'The call for your own inventions did not come back. Nothing here is out of date — there is simply nothing to show until it succeeds.'}
          action={
            <button
              onClick={refetch}
              className="inline-flex items-center gap-2 rounded-lg bg-iflow-700 px-4 py-2 text-sm font-medium text-white hover:bg-iflow-800"
            >
              <RefreshCw size={16} /> Retry
            </button>
          }
        />
      ) : (
        <ViewBody view={view} projects={projects ?? []} owned={owned} />
      )}
    </div>
  )
}

function ViewBody({
  view,
  projects,
  owned,
}: {
  view: OwnScopeView
  projects: Project[]
  owned: Project[]
}) {
  switch (view) {
    case 'atlas':
      return <MyInventionsAtlas projects={projects} />
    case 'overlap':
      return <OwnOverlapView projects={projects} />
    case 'synergy':
      return <OwnSynergyView owned={owned} />
    case 'shape':
      return <OwnShapeStrategyView owned={owned} />
    case 'watch':
      return <OwnWatchView owned={owned} />
    case 'list':
    default:
      // The existing pipeline table, unchanged, minus its own page header —
      // this screen already rendered the one the figure shows.
      return <ProjectsTable projects={projects} showHeader={false} />
  }
}
